/**
 * useDocumentUpload - Shared Hook for Document Upload and Extraction
 * TASK-220: Phase 3 - Frontend Upload Unification
 *
 * Handles file upload and data extraction for:
 * - KYC documents (passports, IDs, address proofs)
 * - Portfolio statements (bank positions, transactions)
 *
 * Features:
 * - Direct extraction (no staging/approval workflow)
 * - Progress tracking for upload and extraction phases
 * - Error handling with user-friendly messages
 * - TypeScript types for extracted data
 */

import { useState, useCallback } from 'react';
import { httpClientUpload } from '../lib/httpClient';

// ============= Types =============

export type ExtractionType = 'kyc' | 'portfolio';

export interface UseDocumentUploadOptions {
  clientId: string;
  extractionType: ExtractionType;
  portfolioId?: string;  // TASK-278: Portfolio ID for database persistence
  skipDatabase?: boolean;
  onProgress?: (progress: number) => void;
  onExtracted?: (data: ExtractedData) => void;
  onError?: (error: string) => void;
}

// KYC extraction response
export interface KycExtractedData {
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  nationality?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  document_type?: string;
  document_number?: string;
  expiry_date?: string;
  phone?: string;
  email?: string;
  [key: string]: string | undefined;
}

// Portfolio position from extraction
export interface PortfolioPosition {
  isin?: string;
  security_name: string;
  asset_class?: string;
  security_type?: string;
  quantity?: number;
  price?: number;
  market_value?: number;
  currency?: string;
  weight?: number;
  extraction_confidence?: number;
  [key: string]: string | number | undefined;
}

// Portfolio cash position
export interface CashPosition {
  currency: string;
  amount: number;
  account_type?: string;
}

// Portfolio extraction response
export interface PortfolioExtractedData {
  positions: PortfolioPosition[];
  cash_positions: CashPosition[];
  confidence_score: number;
  detected_bank?: string;
  warnings: string[];
  errors: string[];
  message: string;
}

// Union type for extracted data
export type ExtractedData = KycExtractedData | PortfolioExtractedData;

export interface UseDocumentUploadReturn {
  upload: (file: File) => Promise<void>;
  extractedData: ExtractedData | null;
  isUploading: boolean;
  isExtracting: boolean;
  progress: number;
  error: string | null;
  reset: () => void;
}

// ============= Hook Implementation =============

export function useDocumentUpload(options: UseDocumentUploadOptions): UseDocumentUploadReturn {
  const {
    clientId,
    extractionType,
    portfolioId,  // TASK-278: Extract portfolioId
    skipDatabase = false,
    onProgress,
    onExtracted,
    onError
  } = options;

  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setExtractedData(null);
    setIsUploading(false);
    setIsExtracting(false);
    setProgress(0);
    setError(null);
  }, []);

  const upload = useCallback(async (file: File) => {
    reset();
    setIsUploading(true);

    try {
      // Validate file type
      const validExtensions = extractionType === 'kyc'
        ? ['.pdf', '.jpg', '.jpeg', '.png']
        : ['.pdf', '.xlsx', '.xls', '.csv'];

      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        throw new Error(
          `Unsupported file type: ${fileExtension}. ` +
          `Allowed: ${validExtensions.join(', ')}`
        );
      }

      // Validate file size (50MB max)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(
          `File too large: ${Math.round(file.size / (1024 * 1024))}MB. ` +
          `Maximum: ${Math.round(maxSize / (1024 * 1024))}MB`
        );
      }

      // Build FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('client_id', clientId);

      // TASK-278: Include portfolio_id for database persistence
      if (extractionType === 'portfolio' && portfolioId) {
        formData.append('portfolio_id', portfolioId);
      }

      if (extractionType === 'portfolio' && skipDatabase) {
        formData.append('skip_database', 'true');
      }

      // Update progress - uploading phase (0-50%)
      setProgress(10);
      onProgress?.(10);

      // Determine endpoint based on extraction type
      const endpoint = extractionType === 'kyc'
        ? `/api/clients/${clientId}/extract-profile`
        : '/api/data-ingestion/portfolio/positions/extract';

      setProgress(30);
      onProgress?.(30);

      // Upload and extract
      setIsUploading(false);
      setIsExtracting(true);
      setProgress(50);
      onProgress?.(50);

      const rawClient = httpClientUpload.getRawClient();
      const response = await rawClient.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            // Upload progress is 0-50%
            const uploadProgress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 50
            );
            setProgress(uploadProgress);
            onProgress?.(uploadProgress);
          }
        },
      });

      // Extraction complete (50-100%)
      setProgress(100);
      onProgress?.(100);
      setIsExtracting(false);

      // Handle response based on extraction type
      if (extractionType === 'kyc') {
        // KYC extraction returns kyc_data
        const kycData: KycExtractedData = response.data.kyc_data || response.data;
        setExtractedData(kycData);
        onExtracted?.(kycData);
      } else {
        // Portfolio extraction returns positions and cash_positions
        const portfolioData: PortfolioExtractedData = {
          positions: response.data.positions || [],
          cash_positions: response.data.cash_positions || [],
          confidence_score: response.data.confidence_score || 0,
          detected_bank: response.data.detected_bank,
          warnings: response.data.warnings || [],
          errors: response.data.errors || [],
          message: response.data.message || 'Extraction complete'
        };
        setExtractedData(portfolioData);
        onExtracted?.(portfolioData);
      }

    } catch (err: any) {
      setIsUploading(false);
      setIsExtracting(false);
      setProgress(0);

      // Extract error message
      let errorMessage = 'Upload failed';
      if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      onError?.(errorMessage);
      console.error('[useDocumentUpload] Error:', errorMessage, err);
    }
  }, [clientId, extractionType, portfolioId, skipDatabase, onProgress, onExtracted, onError, reset]);

  return {
    upload,
    extractedData,
    isUploading,
    isExtracting,
    progress,
    error,
    reset
  };
}

// Type guard helpers
export function isKycData(data: ExtractedData | null): data is KycExtractedData {
  if (!data) return false;
  return !('positions' in data);
}

export function isPortfolioData(data: ExtractedData | null): data is PortfolioExtractedData {
  if (!data) return false;
  return 'positions' in data;
}
